import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import CameraTest from './CameraTest';
import FireExtinguisherCamera from './FireExtinguisherCamera';
import AutoCaptureCamera from './AutoCaptureCamera';
import PWAInstallPrompt from './PWAInstallPrompt';
import GamificationDisplay from './GamificationDisplay';
import OnboardingWizard from './OnboardingWizard';
import AdminPanel from './AdminPanel';
import LeaderboardPage from './LeaderboardPage';
import LocationSelector from './LocationSelector';
import SuccessModal from './components/SuccessModal';
import InfoRequestModal from './components/InfoRequestModal';
import ProfileSettingsModal from './components/ProfileSettingsModal';
import ConfirmationModal from './components/ConfirmationModal';
import NotesAttachmentsModal from './components/NotesAttachmentsModal';
import UserSubmissionsModal from './components/UserSubmissionsModal';
import useOnboarding from './hooks/useOnboarding';
import Vapi from '@vapi-ai/web';
import backgroundImage from './assets/background.png';
import { 
  ATTRIBUTION_CONFIG, 
  OAUTH_CONFIG, 
  BACKEND_URL, 
  APP_VERSION
} from './utils/constants';
import * as api from './services/apiService';
import { useAuth } from './hooks/useAuth';
import { useInspections } from './hooks/useInspections';
import { useSubmission } from './hooks/useSubmission';
import { useUI } from './hooks/useUI';
import { useGamification } from './hooks/useGamification';
import { setupPushListeners, initializePushNotifications } from './services/pushNotificationService';
import { extractAdditionalNotes } from './utils/inspectionHelper';
import { formatDate } from './utils/dateHelper';
import { generateCSV, downloadCSV } from './utils/csvHelper';
import Header from './components/layout/Header';
import BottomNav from './components/layout/BottomNav';
import ModalManager from './components/layout/ModalManager';
import UserInbox from './components/UserInbox';
import UserMapModal from './components/UserMapModal';
import DashboardPage from './pages/DashboardPage';
import DirectSubmissionPage from './pages/DirectSubmissionPage';
// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import ApprovalWorkflow from './pages/admin/ApprovalWorkflow';
import EmailDigestManagement from './pages/admin/EmailDigestManagement';
import RegionalOfficersManagement from './pages/admin/RegionalOfficersManagement';
import AnalyticsReports from './pages/admin/AnalyticsReports';
import ArchiveView from './pages/admin/ArchiveView';
import TrashView from './pages/admin/TrashView';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ProtectedUserRoute from './components/auth/ProtectedUserRoute';
import SalesLeadActionPage from './pages/SalesLeadActionPage';
import ConversionMetrics from './pages/admin/ConversionMetrics';
import CampaignManagement from './pages/admin/CampaignManagement';
import GmailStyleInbox from './components/admin/GmailStyleInbox';
// DISABLED FOR STAGING - Microsoft auth causing crypto errors
// import { loginWithMicrosoftPopup, prettyMsalError} from './msalHelper';
import './App.css';

const App = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  
  // 🎬 Render Progress State
  const [showRenderProgress, setShowRenderProgress] = useState(false);
  const [renderStep, setRenderStep] = useState('capture');
  
  // Onboarding state management (extracted to custom hook)
  const { showOnboarding, setShowOnboarding, completeOnboarding } = useOnboarding();
  
  // Auth state and functions (extracted to useAuth hook)
  const {
    user,
    setUser,
    loading,
    setLoading,
    currentUser,
    setCurrentUser,
    submittedOnBehalfOf,
    setSubmittedOnBehalfOf,
    userProfile,
    setUserProfile,
    showSubmittedByModal,
    setShowSubmittedByModal,
    showProfileModal,
    setShowProfileModal,
    showProfileDropdown,
    setShowProfileDropdown,
    authModalMode,
    setAuthModalMode,
    showAttributionModal,
    setShowAttributionModal,
    loadUserProfile,
    saveUserProfile,
    getFirstNameFromEmail,
    getSubmittedByName,
    validatePyeBarkerEmail,
    setSubmittedOnBehalfOfUser,
    clearSubmittedOnBehalf,
    logoutUser,
    checkAttributionRequired,
    handleQuickAttribution,
    skipAttribution,
    handleGoogleOAuth,
    handleMicrosoftOAuth
  } = useAuth();
  
  // Inspections state and functions (extracted to useInspections hook)
  const {
    inspections,
    setInspections,
    dueInspections,
    setDueInspections,
    editingInspection,
    setEditingInspection,
    editFormData,
    setEditFormData,
    focusedEditMode,
    setFocusedEditMode,
    currentInspectionIndex,
    setCurrentInspectionIndex,
    modalImage,
    setModalImage,
    pendingOpenInspectionId,
    setPendingOpenInspectionId,
    dataTablePageSize,
    setDataTablePageSize,
    dataTableCurrentPage,
    setDataTableCurrentPage,
    loadInspections,
    loadDueInspections,
    refreshInspections,
    saveInspectionChanges,
    deleteInspection,
    approveInspection,
    toggleHumanReview,
    startEditingInspection,
    cancelEditing,
    navigateToNextInspection,
    navigateToPrevInspection,
    exitFocusedMode,
    getPaginatedInspections
  } = useInspections();
  
  // Submission workflow state and functions (extracted to useSubmission hook)
  const {
    selectedImage,
    setSelectedImage,
    capturedImages,
    setCapturedImages,
    location,
    setLocation,
    businessName,
    setBusinessName,
    selectedLocations,
    setSelectedLocations,
    gpsData,
    setGpsData,
    isCapturingGPS,
    setIsCapturingGPS,
    gpsError,
    setGpsError,
    nearestOffice,
    setNearestOffice,
    loadingNearest,
    setLoadingNearest,
    businessSuggestions,
    setBusinessSuggestions,
    isLoadingSuggestions,
    setIsLoadingSuggestions,
    showSuggestions,
    setShowSuggestions,
    showLocationPopup,
    setShowLocationPopup,
    locationSelectedFromPopup,
    setLocationSelectedFromPopup,
    notes,
    setNotes,
    isSubmitting,
    setIsSubmitting,
    inspectionResult,
    setInspectionResult,
    isAnalyzing,
    setIsAnalyzing,
    showProcessingModal,
    setShowProcessingModal,
    analyzeProgress,
    setAnalyzeProgress,
    showConfirmationPopup,
    setShowConfirmationPopup,
    confirmationData,
    setConfirmationData,
    showSuccessPopup,
    setShowSuccessPopup,
    successPopupData,
    setSuccessPopupData,
    additionalNotes,
    setAdditionalNotes,
    additionalImages,
    setAdditionalImages,
    quickShotOtherNotes,
    setQuickShotOtherNotes,
    mainPageAttachments,
    setMainPageAttachments,
    consecutiveScans,
    setConsecutiveScans,
    captureGPS,
    fetchBusinessSuggestions,
    handleFileUpload,
    handleMainPageFileUpload,
    handleQuickShotSubmit,
    analyzeImage
  } = useSubmission();
  
  // UI state and functions (extracted to useUI hook)
  const {
    toasts,
    setToasts,
    soundDebug,
    setSoundDebug,
    soundEnabled,
    setSoundEnabled,
    audioRefs,
    showUpdateNotification,
    setShowUpdateNotification,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    submissionBanner,
    setSubmissionBanner,
    isQuickLocationOpen,
    setIsQuickLocationOpen,
    showAdditionalNotes,
    setShowAdditionalNotes,
    showAttachmentModal,
    setShowAttachmentModal,
    currentAttachment,
    setCurrentAttachment,
    showToast,
    removeToast,
    playSound,
    toggleSound
  } = useUI();
  
  // Gamification state and functions (extracted to useGamification hook)
  const {
    showGamification,
    setShowGamification,
    userGamificationStats,
    setUserGamificationStats,
    loadingGamificationStats,
    setLoadingGamificationStats,
    leaderboardPageSize,
    setLeaderboardPageSize,
    leaderboardCurrentPage,
    setLeaderboardCurrentPage,
    showUserSubmissionsModal,
    setShowUserSubmissionsModal,
    selectedUserForSubmissions,
    setSelectedUserForSubmissions,
    getUserTotalPoints,
    getUserRank,
    getPointsLeaderboard,
    getResponsiveLeaderboardCount,
    getPaginatedLeaderboard,
    fetchUserGamificationStats,
    awardGamificationPoints
  } = useGamification();
  
  const [isTestingCamera, setIsTestingCamera] = useState(false);
  const [isFireExtinguisherCameraOpen, setIsFireExtinguisherCameraOpen] = useState(false);
  const [useAutoCapture, setUseAutoCapture] = useState(false); // Use regular camera (like production)
  const [showPyeGuide, setShowPyeGuide] = useState(() => {
    const completed = localStorage.getItem('pyeGuideCompleted');
    return completed !== 'true'; // Show guide if not completed
  });
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [selectedUserEmailForSubmissions, setSelectedUserEmailForSubmissions] = useState(null);
  const fileInputRef = useRef(null);
  const mainPageFileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const hasAutoOpenedCamera = useRef(false);
  
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  
  // ### ATTRIBUTION SYSTEM CONFIGURATION ###
  const attributionConfig = ATTRIBUTION_CONFIG;
  
  // Attribution Mode State (local to App.js)
  const [attributionMode, setAttributionMode] = useState('quick'); // 'quick', 'google', 'microsoft', 'email'
  
  // Quick Shot Mode State
  const [isQuickShotMode, setIsQuickShotMode] = useState(true);
  const [submittedBy, setSubmittedBy] = useState('');
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [isProcessingOffline, setIsProcessingOffline] = useState(false);
  
  // 🔔 User Inbox state (for bottom nav)
  const [showUserInboxModal, setShowUserInboxModal] = useState(false);
  const [userInboxUnreadCount, setUserInboxUnreadCount] = useState(0);
  
  // 🗺️ User Map Modal state (for bottom nav)
  const [showMapModal, setShowMapModal] = useState(false);
  
  // 📝 Info Request Modal state (low confidence / need more info)
  const [showInfoRequestModal, setShowInfoRequestModal] = useState(false);
  const [infoRequestData, setInfoRequestData] = useState(null);

  // Resolve the *actual* user email for notifications/inbox.
  // NOTE: `currentUser` is often a display name (e.g. "Matt Hales"), not an email.
  const getNotificationUserEmail = () => {
    const candidates = [];
    // Highest priority: explicit "submitted on behalf of" when it's an email
    candidates.push(submittedOnBehalfOf);
    // Next: saved user profile email (quick attribution supports optional email)
    candidates.push(userProfile && userProfile.email);
    // Next: persisted attribution email (legacy)
    try {
      candidates.push(localStorage.getItem('submitted_on_behalf_of'));
    } catch (e) {
      // ignore
    }
    // Next: persisted profile email
    try {
      const raw = localStorage.getItem('user_profile');
      if (raw) {
        const parsed = JSON.parse(raw);
        candidates.push(parsed && parsed.email);
      }
    } catch (e) {
      // ignore
    }
    // Last resort: only accept currentUser if it already looks like an email
    candidates.push(currentUser);

    for (const c of candidates) {
      if (!c || typeof c !== 'string') continue;
      const email = c.trim().toLowerCase();
      if (email.includes('@')) return email;
    }
    return '';
  };

  // 🔔 Poll user inbox count for bottom nav badge
  useEffect(() => {
    const fetchInboxCount = async () => {
      const userEmail = getNotificationUserEmail();
      if (!userEmail || routerLocation.pathname.startsWith('/admin')) return;
      
      // Check if user is authenticated (has MSAL token)
      const token = localStorage.getItem('token');
      if (!token) {
        // Not authenticated yet - skip fetching (avoid 401 spam)
        return;
      }
      
      try {
        const data = await api.fetchUserNotificationsCount(userEmail);
        setUserInboxUnreadCount(data.unread_count || 0);
      } catch (error) {
        // Silently fail on 401 (expected when token expires)
        if (!error.message || !error.message.includes('401')) {
          console.error('❌ Error fetching inbox count:', error);
        }
      }
    };
    
    // Initial fetch
    fetchInboxCount();
    
    // Poll every 30 seconds
    const interval = setInterval(fetchInboxCount, 30000);
    
    return () => clearInterval(interval);
  }, [submittedOnBehalfOf, currentUser, routerLocation.pathname]);

  
  // DEMO: Confirmation popup state
  const [showConfirmationNotesPopup, setShowConfirmationNotesPopup] = useState(false);
  
  // BLOCKED OUT - Vapi.ai Voice Agent Configuration (Not ready for premiere)
  /*
  const vapiConfig = {
    publicKey: 'e73916ba-515a-4a6e-85ff-dc208385eb9e',
    assistantId: '1ec0bb05-5e47-4f1d-9857-03e1c5d793ec'
  };
  */
  
  // BLOCKED OUT - Vapi.ai state (Not ready for premiere)
  // const [vapiInstance, setVapiInstance] = useState(null);
  // const [isCallActive, setIsCallActive] = useState(false);
  
  
  // Custom business entry toggle (local to App.js)
  const [isCustomBusinessEntry, setIsCustomBusinessEntry] = useState(false);
  
  // Additional submission state (local to App.js)
  const [isSubmittingAdditional, setIsSubmittingAdditional] = useState(false);

  // OAuth Configuration (for localhost development)
  const oauthConfig = OAUTH_CONFIG;

  const backendUrl = BACKEND_URL;

  // ### ATTRIBUTION FORM COMPONENTS ###
  
  // Quick Attribution Form (name + optional email)
  const QuickAttributionForm = ({ onSubmit, onCancel, allowSkip }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    
    const handleSubmit = (e) => {
      e.preventDefault();
      if (!name.trim()) {
        showToast('⚠️ Please enter your name.', 'error');
        return;
      }
      onSubmit(name, email);
    };
    
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-white text-sm font-medium mb-2">
            Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:border-white/40 focus:outline-none"
            placeholder="Enter your name"
            required
          />
        </div>
        <div>
          <label className="block text-white text-sm font-medium mb-2">
            Email <span className="text-white/40">(optional)</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:border-white/40 focus:outline-none"
            placeholder="Enter your email"
          />
        </div>
        <div className="flex space-x-3">
          <button
            type="submit"
            className="flex-1 bg-green-500 text-white font-bold py-3 px-4 rounded-lg hover:bg-green-600 transition-colors"
          >
            ✅ Save Profile
          </button>
          {allowSkip && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Skip
            </button>
          )}
        </div>
      </form>
    );
  };

  // 🔐 GOOGLE OAUTH INTEGRATION
  const GoogleAuthForm = ({ onSuccess, onCancel }) => {
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [authError, setAuthError] = useState(null);

    const handleGoogleAuth = async () => {
      setIsAuthenticating(true);
      setAuthError(null);
      
      // 🚨 SAFETY: Auto-reset authenticating state after 10 seconds to prevent stuck state
      const timeoutId = setTimeout(() => {
        console.warn('⚠️ Google auth timeout - resetting authenticating state');
        setIsAuthenticating(false);
        setAuthError('Authentication timed out. Please try again.');
      }, 10000);
      
      try {
        // Check if Google Identity Services is loaded
        if (!window.google || !window.google.accounts) {
          throw new Error('Google OAuth SDK not loaded. Please check your internet connection.');
        }

        // Initialize Google OAuth
        window.google.accounts.id.initialize({
          client_id: '896517772559-r3n8p2bst6pf8c8v2u8c8t0h4e4g0p8l.apps.googleusercontent.com', // Demo client ID - replace with real one
          callback: (response) => {
            clearTimeout(timeoutId); // Clear timeout on success
            try {
              // Decode the JWT token to get user info
              const payload = JSON.parse(atob(response.credential.split('.')[1]));
              
              const profile = {
                name: payload.name,
                email: payload.email,
                avatar: payload.picture,
                type: 'google',
                provider_id: payload.sub
              };
              
              onSuccess(profile);
              setIsAuthenticating(false);
            } catch (error) {
              console.error('❌ Error processing Google OAuth response:', error);
              setAuthError('Failed to process authentication response');
              setIsAuthenticating(false);
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        // Trigger the OAuth popup
        window.google.accounts.id.prompt();
        
      } catch (error) {
        clearTimeout(timeoutId); // Clear timeout on error
        console.error('❌ Google OAuth error:', error);
        setAuthError(error.message || 'Google authentication failed');
        setIsAuthenticating(false);
      }
    };

    return (
      <div className="text-center py-6">
        <div className="mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🔍</span>
          </div>
          <h4 className="text-white text-lg font-semibold mb-2">Sign in with Google</h4>
          <p className="text-white/70 text-sm">Use your Google account for quick setup</p>
        </div>

        {authError && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
            <div className="flex justify-between items-start">
              <p className="text-red-400 text-sm flex-1">❌ {authError}</p>
              <button
                onClick={() => {
                  setAuthError(null);
                  setIsAuthenticating(false);
                }}
                className="ml-2 text-red-400 hover:text-red-300 text-xs underline"
                title="Clear error and reset"
              >
                Reset
              </button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleGoogleAuth}
            disabled={isAuthenticating}
            className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105"
          >
            {isAuthenticating ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Connecting to Google...</span>
              </div>
            ) : (
              <>🔍 Continue with Google</>
            )}
          </button>
          
          <button
            onClick={onCancel}
            className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
          >
            ← Back to Quick Setup
          </button>
        </div>
      </div>
    );
  };

  // 🔐 MICROSOFT OAUTH INTEGRATION  
  const MicrosoftAuthForm = ({ onSuccess, onCancel }) => {
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [authError, setAuthError] = useState(null);

    const handleMicrosoftAuth = async () => {
      setIsAuthenticating(true);
      setAuthError(null);

      // 🚨 SAFETY: Auto-reset authenticating state after 10 seconds to prevent stuck state
      const timeoutId = setTimeout(() => {
        console.warn('⚠️ Microsoft auth timeout - resetting authenticating state');
        setIsAuthenticating(false);
        setAuthError('Authentication timed out. Please try again.');
      }, 10000);

      try {
        // 🔥 REAL MICROSOFT MSAL INTEGRATION - DISABLED FOR STAGING
        console.log('⚠️ Microsoft auth disabled for staging');
        throw new Error('Microsoft authentication is disabled');
        
        // COMMENTED OUT - Causing crypto errors on staging
        /*
        console.log('🚀 MicrosoftAuthForm: Starting Microsoft MSAL authentication...');
        console.log('🔧 MicrosoftAuthForm: Checking if loginWithMicrosoftPopup is available:', typeof loginWithMicrosoftPopup);
        
        // 1) MSAL popup → get ID token
        const result = await loginWithMicrosoftPopup();
        const idToken = result.idToken;
        const userName = result.account?.name;
        const userEmail = 
          result.idTokenClaims?.email ||
          result.idTokenClaims?.preferred_username ||
          result.account?.username;

        if (!idToken) {
          throw new Error("No ID token returned from Microsoft.");
        }

        console.log('✅ Microsoft MSAL popup successful, sending to backend...');

        // 2) Send ID token to backend to create session
        const response = await fetch(`${BACKEND_URL}/api/auth/microsoft`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Include cookies
          body: JSON.stringify({ id_token: idToken })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Backend authentication failed');
        }

        const authData = await response.json();
        console.log('✅ Backend authentication successful');

        // 3) Create profile for frontend
        const profile = {
          name: userName || authData.user?.name || 'Microsoft User',
          email: userEmail || authData.user?.email,
          avatar: 'https://via.placeholder.com/150', // Microsoft doesn't provide avatar in basic scopes
          type: 'microsoft',
          provider_id: authData.user?.id || 'microsoft_' + Date.now()
        };
        
        clearTimeout(timeoutId); // Clear timeout on success
        onSuccess(profile);
        setIsAuthenticating(false);
        */
        
      } catch (error) {
        clearTimeout(timeoutId); // Clear timeout on error
        console.error('❌ Microsoft OAuth error:', error);
        setAuthError(error.message || 'Authentication failed');
        setIsAuthenticating(false);
      }
    };

    return (
      <div className="text-center py-6">
        <div className="mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">💼</span>
          </div>
          <h4 className="text-white text-lg font-semibold mb-2">Sign in with Microsoft</h4>
          <p className="text-white/70 text-sm">Use your Microsoft account for enterprise setup</p>
        </div>

        {authError && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
            <div className="flex justify-between items-start">
              <p className="text-red-400 text-sm flex-1">❌ {authError}</p>
              <button
                onClick={() => {
                  setAuthError(null);
                  setIsAuthenticating(false);
                }}
                className="ml-2 text-red-400 hover:text-red-300 text-xs underline"
                title="Clear error and reset"
              >
                Reset
              </button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleMicrosoftAuth}
            disabled={isAuthenticating}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105"
          >
            {isAuthenticating ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Connecting to Microsoft...</span>
              </div>
            ) : (
              <>💼 Continue with Microsoft</>
            )}
          </button>
          
          <button
            onClick={onCancel}
            className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
          >
            ← Back to Quick Setup
          </button>
        </div>
      </div>
    );
  };

  // 📧 EMAIL/PASSWORD AUTHENTICATION INTEGRATION
  const EmailAuthForm = ({ onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({ name: '', email: '', password: '' });
    const [isRegistering, setIsRegistering] = useState(true);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [authError, setAuthError] = useState(null);

    const handleEmailAuth = async (e) => {
      e.preventDefault();
      setIsAuthenticating(true);
      setAuthError(null);

      // 🚨 SAFETY: Auto-reset authenticating state after 10 seconds to prevent stuck state
      const timeoutId = setTimeout(() => {
        console.warn('⚠️ Email auth timeout - resetting authenticating state');
        setIsAuthenticating(false);
        setAuthError('Authentication timed out. Please try again.');
      }, 10000);

      try {
        // Validation
        if (!formData.email || !formData.password) {
          throw new Error('Email and password are required');
        }
        
        if (isRegistering && !formData.name) {
          throw new Error('Name is required for registration');
        }

        if (formData.password.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }

        // For MVP demo - simulate email authentication
        // In production, you would integrate with your auth backend
        
        await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate network request
        
        const profile = {
          name: formData.name || formData.email.split('@')[0],
          email: formData.email,
          avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${formData.name || formData.email}`,
          type: 'email',
          provider_id: 'email_' + Date.now(),
          auth_method: isRegistering ? 'register' : 'login'
        };
        
        clearTimeout(timeoutId); // Clear timeout on success
        onSuccess(profile);
        setIsAuthenticating(false);
        
      } catch (error) {
        clearTimeout(timeoutId); // Clear timeout on error
        console.error(`❌ Email authentication error:`, error);
        setAuthError(error.message || 'Authentication failed');
        setIsAuthenticating(false);
      }
    };

    return (
      <div className="py-4">
        <div className="mb-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">📧</span>
          </div>
          <h4 className="text-white text-lg font-semibold mb-2">
            {isRegistering ? 'Create Account' : 'Sign In'}
          </h4>
          <p className="text-white/70 text-sm">
            {isRegistering 
              ? 'Create your fire safety account' 
              : 'Sign in to your existing account'
            }
          </p>
        </div>

        {authError && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
            <div className="flex justify-between items-start">
              <p className="text-red-400 text-sm flex-1">❌ {authError}</p>
              <button
                onClick={() => {
                  setAuthError(null);
                  setIsAuthenticating(false);
                }}
                className="ml-2 text-red-400 hover:text-red-300 text-xs underline"
                title="Clear error and reset"
              >
                Reset
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleEmailAuth} className="space-y-4">
          {isRegistering && (
            <div>
              <label className="block text-white/80 text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                placeholder="Your full name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                required={isRegistering}
              />
            </div>
          )}
          
          <div>
            <label className="block text-white/80 text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-white/80 text-sm font-medium mb-1">Password</label>
            <input
              type="password"
              placeholder={isRegistering ? 'Create password (6+ chars)' : 'Your password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
              required
              minLength="6"
            />
          </div>

          <div className="space-y-3 pt-2">
            <button
              type="submit"
              disabled={isAuthenticating}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold py-3 px-6 rounded-lg hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105"
            >
              {isAuthenticating ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>{isRegistering ? 'Creating Account...' : 'Signing In...'}</span>
                </div>
              ) : (
                <>📧 {isRegistering ? 'Create Account' : 'Sign In'}</>
              )}
            </button>
            
            <button
              type="button"
              onClick={() => setIsRegistering(!isRegistering)}
              className="w-full text-white/70 hover:text-white text-sm underline transition-colors"
            >
              {isRegistering 
                ? 'Already have an account? Sign in' 
                : "Don't have an account? Sign up"
              }
            </button>
            
            <button
              type="button"
              onClick={onCancel}
              className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
            >
              ← Back to Quick Setup
            </button>
          </div>
        </form>
      </div>
    );
  };

  // CSV Export Functions

  // Load offline queue on startup and set up connection monitoring
  useEffect(() => {
    loadOfflineQueue();
    
    // Process offline queue when app loads (in case we're back online)
    const processOnStartup = () => {
      setTimeout(() => {
        if (navigator.onLine) {
          processOfflineQueue();
        }
      }, 2000); // Wait 2 seconds for app to fully load
    };
    
    processOnStartup();
    
    // Listen for online/offline events
    const handleOnline = () => {
      console.log('📡 Connection restored, processing offline queue...');
      processOfflineQueue();
    };
    
    const handleOffline = () => {
      console.log('📱 Connection lost, submissions will be queued offline');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);


  const handleLogin = async () => {
    try {
      setLoading(true);
      
      const data = await api.handleLogin();
      console.log('Demo auth successful');
      localStorage.setItem('session_token', data.session_token);
      setUser(data.user);
      navigate('/');
    } catch (error) {
      console.error('Demo authentication failed:', error);
      showToast('❌ Authentication failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthCallback = async () => {
    const hash = window.location.hash;
    const sessionId = hash.split('session_id=')[1];
    
    if (sessionId) {
      try {
        console.log('Processing session ID:', sessionId);
        
        const data = await api.checkSession(sessionId);
        console.log('Auth successful');
        localStorage.setItem('session_token', data.session_token);
        setUser(data.user);
        navigate('/');
        // Clear the hash from URL
        window.history.replaceState({}, document.title, '/');
      } catch (error) {
        console.error('Authentication failed:', error);
        showToast('❌ Authentication failed. Please try again.', 'error');
      }
    } else {
      console.log('No session ID found in hash:', hash);
    }
  };

  const closeCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => {
        track.stop();
      });
      videoRef.current.srcObject = null;
    }
    setIsCameraOpen(false);
  };

  const openCamera = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        showToast('⚠️ Camera access is not supported by this browser. Please try uploading a file instead.', 'warning');
        return;
      }
  
      const constraints = {
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          setIsCameraOpen(true);
        };
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      let errorMessage = 'Unable to access camera. ';
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage += 'Please allow camera permission in your browser settings and try again.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage += 'No camera found on this device.';
      } else if (error.name === 'NotReadableError') {
        errorMessage += 'Camera is already in use by another application.';
      } else {
        errorMessage += 'An unexpected error occurred.';
      }
      showToast(`❌ ${errorMessage}`, 'error');
    }
  };

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!video || !canvas || !video.srcObject) return;
    
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    
    const imageData = canvas.toDataURL('image/jpeg');
    setSelectedImage(imageData);
    closeCamera();
  };

  // Offline Storage Functions
  const saveToOfflineQueue = (submissionData) => {
    try {
      const queue = JSON.parse(localStorage.getItem('offlineSubmissions') || '[]');
      const queueItem = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        data: submissionData,
        retries: 0,
        maxRetries: 3
      };
      queue.push(queueItem);
      localStorage.setItem('offlineSubmissions', JSON.stringify(queue));
      setOfflineQueue(queue);
      console.log('💾 Saved submission to offline queue');
    } catch (error) {
      console.error('Failed to save to offline queue:', error);
    }
  };

  const processOfflineQueue = async () => {
    if (isProcessingOffline) return;
    
    try {
      setIsProcessingOffline(true);
      const queue = JSON.parse(localStorage.getItem('offlineSubmissions') || '[]');
      
      if (queue.length === 0) {
        setOfflineQueue([]);
        return;
      }
      
      console.log(`📡 Processing ${queue.length} offline submissions...`);
      const sessionToken = localStorage.getItem('session_token');
      const remainingQueue = [];
      
      for (const item of queue) {
        try {
          const response = await api.createInspection(item.data);
          
          if (response.ok) {
            console.log(`✅ Successfully uploaded offline submission ${item.id}`);
          } else {
            throw new Error(`HTTP ${response.status}`);
          }
        } catch (error) {
          console.log(`❌ Failed to upload submission ${item.id}, retry ${item.retries + 1}/${item.maxRetries}`);
          item.retries += 1;
          
          if (item.retries < item.maxRetries) {
            remainingQueue.push(item);
          } else {
            console.log(`🗑️ Dropping submission ${item.id} after ${item.maxRetries} retries`);
          }
        }
      }
      
      localStorage.setItem('offlineSubmissions', JSON.stringify(remainingQueue));
      setOfflineQueue(remainingQueue);
      
      if (remainingQueue.length === 0) {
        console.log('🎉 All offline submissions processed successfully!');
        await loadInspections(); // Refresh the list
      }
      
    } catch (error) {
      console.error('Error processing offline queue:', error);
    } finally {
      setIsProcessingOffline(false);
    }
  };

  const loadOfflineQueue = () => {
    try {
      const queue = JSON.parse(localStorage.getItem('offlineSubmissions') || '[]');
      setOfflineQueue(queue);
    } catch (error) {
      console.error('Failed to load offline queue:', error);
      setOfflineQueue([]);
    }
  };

  // BLOCKED OUT - Vapi.ai Voice Agent Initialization (Not ready for premiere)
  /*
  useEffect(() => {
    const initializeVapi = async () => {
      try {
        console.log('🔧 Initializing Vapi.ai with config:', vapiConfig);
        
        // Create new Vapi instance with public key
        const vapi = new Vapi(vapiConfig.publicKey);
        setVapiInstance(vapi);
        
        console.log('✅ Vapi.ai initialized successfully');
        // ### VOICE ASSISTANT TOAST - BLOCKED FOR MVP ###
        // showToast('🎤 Voice assistant ready!', 'success', 2000);
        
        // Set up event listeners
        vapi.on('call-start', () => {
          console.log('📞 Call started');
          setIsCallActive(true);
          showToast('🎤 Voice call started!', 'success', 2000);
        });
        
        vapi.on('call-end', () => {
          console.log('📞 Call ended');
          setIsCallActive(false);
          showToast('📞 Voice call ended', 'info', 2000);
        });
        
        vapi.on('error', (error) => {
          console.error('❌ Vapi error:', error);
          setIsCallActive(false);
          showToast('⚠️ Voice call error: ' + (error.message || 'Unknown error'), 'error', 5000);
        });
        
      } catch (error) {
        console.error('❌ Failed to initialize Vapi.ai:', error);
        showToast('⚠️ Voice assistant initialization failed', 'error', 5000);
      }
    };
    
    // Initialize after a brief delay
    const timer = setTimeout(initializeVapi, 1000);
    return () => clearTimeout(timer);
  }, []);
  */

  // Set background image CSS variable
  useEffect(() => {
    document.documentElement.style.setProperty('--background-image', `url(${backgroundImage})`);
  }, []);

  // Start voice call
  // BLOCKED OUT - Voice call functions (Not ready for premiere)
  /*
  const startVoiceCall = async () => {
    if (!vapiInstance) {
      console.warn('⚠️ Vapi instance not ready');
      showToast('⚠️ Voice assistant not ready', 'error', 2000);
      return;
    }

    try {
      console.log('📞 Starting voice call with assistant:', vapiConfig.assistantId);
      showToast('🎤 Starting voice call...', 'info', 2000);
      
      await vapiInstance.start(vapiConfig.assistantId);
      
      console.log('✅ Voice call started successfully');
      
    } catch (error) {
      console.error('❌ Error starting voice call:', error);
      showToast('⚠️ Failed to start voice call: ' + (error.message || 'Unknown error'), 'error', 5000);
    }
  };

  // End voice call
  const endVoiceCall = async () => {
    if (vapiInstance && isCallActive) {
      try {
        await vapiInstance.stop();
        console.log('📞 Voice call ended');
      } catch (error) {
        console.error('❌ Error ending voice call:', error);
        showToast('⚠️ Failed to end voice call: ' + (error.message || 'Unknown error'), 'error', 5000);
      }
    }
  };
  */



  // Quick Shot Submit Function
  // PILOT: submitted_on_behalf_of functions (replaces login)
  // Fetch gamification stats when user changes
  useEffect(() => {
    if (submittedOnBehalfOf) {
      fetchUserGamificationStats(submittedOnBehalfOf);
    }
  }, [submittedOnBehalfOf]);
  
  // Sync submittedBy with currentUser from auth hook
  useEffect(() => {
    if (currentUser && currentUser !== 'Not logged in' && currentUser !== submittedBy) {
      setSubmittedBy(currentUser);
      console.log('🔄 Synced submittedBy with currentUser');
    }
  }, [currentUser]);
  
  // Reset submission state when navigating to homepage
  useEffect(() => {
    if (routerLocation.pathname === '/') {
      // Clear ALL submission state for clean camera restart
      setShowLocationPopup(false);
      setShowConfirmationPopup(false);
      setShowSuccessPopup(false);
      setSuccessPopupData(null);
      setConfirmationData(null);
      setSelectedImage(null);
      setCapturedImages([]);
      setInspectionResult(null);
      
      console.log('🏠 Homepage: Reset all submission state');
    }
  }, [routerLocation.pathname]);
  
  // 📝 Info Request Modal - Global callback for low confidence scans
  useEffect(() => {
    window.showInfoRequestModal = (aiData) => {
      setInfoRequestData(aiData);
      setShowInfoRequestModal(true);
    };
    
    return () => {
      delete window.showInfoRequestModal;
    };
  }, []);
  
  // 🔔 Initialize push notifications after user logs in
  useEffect(() => {
    const initPush = async () => {
      // Only initialize if user is logged in
      const userEmail = getNotificationUserEmail();
      if (!userEmail || !userEmail.includes('@')) return;
      
      try {
        // Setup listeners (only once)
        setupPushListeners(BACKEND_URL, navigate, () => {
          // Refresh inbox count callback
          console.log('🔄 Inbox count refresh triggered');
        });
        
        // Request push permission and register device
        const result = await initializePushNotifications(userEmail, BACKEND_URL);
        if (result.success) {
          console.log('✅ Push notifications initialized');
        }
      } catch (error) {
        console.log('⚠️ Push notifications not available:', error.message);
      }
    };
    
    if (!showOnboarding) {
      initPush();
    }
  }, [submittedOnBehalfOf, currentUser, showOnboarding, navigate]);
  
  // Auto-open camera when landing on dashboard (after onboarding complete)
  useEffect(() => {
    // ONLY auto-open if: on dashboard, onboarding done, camera closed, no popups, AND no active submission
    const noPopupsActive = !showLocationPopup && !showConfirmationPopup && !showSuccessPopup;
    const noActiveSubmission = !selectedImage && capturedImages.length === 0; // No image = not in submission flow
    
    const currentPath = routerLocation.pathname;
    if (!showOnboarding && currentPath === '/' && !isFireExtinguisherCameraOpen && noPopupsActive && noActiveSubmission) {
      console.log('🎯 Auto-opening camera on dashboard page...');
      
      // Immediate open (no delay needed)
      setIsFireExtinguisherCameraOpen(true);
      
      // 🔧 FIX: Only auto-capture GPS if permission wasn't granted before (prevents re-prompting on refresh)
      const locationPermissionGranted = localStorage.getItem('locationPermissionGranted');
      if (!gpsData && !isCapturingGPS && !locationPermissionGranted) {
        console.log('📍 First time - requesting GPS...');
        captureGPS();
      } else if (!gpsData && locationPermissionGranted && !isCapturingGPS) {
        // Permission already granted, silently capture GPS without showing prompt
        console.log('📍 Permission already granted - silently capturing GPS...');
        captureGPS();
      }
    }
  }, [routerLocation.pathname, showOnboarding, isFireExtinguisherCameraOpen, showLocationPopup, showConfirmationPopup, showSuccessPopup, selectedImage, capturedImages, gpsData, isCapturingGPS]);

  // DEPRECATED - Use analyzeImage from useSubmission hook instead
  const analyzeImageOLD_DELETE_ME = async () => {
    if (!selectedImage || !location.trim()) {
      showToast('⚠️ Please select an image and enter a location.', 'warning');
      return;
    }

    setIsAnalyzing(true);
    setShowProcessingModal(true);
    setAnalyzeProgress(0);
    setInspectionResult(null);

    try {
      const requestStartedAtMs = Date.now();
      const imageBase64 = selectedImage.split(',')[1]; // Remove data URL prefix
      
      const submitterEmail = (submittedOnBehalfOf && typeof submittedOnBehalfOf === 'string' && submittedOnBehalfOf.includes('@'))
        ? submittedOnBehalfOf.trim().toLowerCase()
        : undefined;
      const response = await api.createInspection({
        image_base64: imageBase64,
        location: location,
        notes: notes,
        submitted_by: submittedBy.trim() || 'Anonymous',
        submitted_by_email: submitterEmail,
        gps_data: gpsData
      });

      if (response.ok) {
        const result = await response.json();
        // Treat success flag if present, but still handle without it
        if (result && result.success) {
          console.log(`Analysis succeeded in ${result.duration_ms} ms using ${result.model}`);
        }
        setInspectionResult(result);
        // Navigate to Validate page and open the freshly created record
        if (result && result.inspection_id) {
          setPendingOpenInspectionId(result.inspection_id);
        }
        setSelectedImage(null);
        setLocation('');
        setNotes('');
        // Keep submittedBy for future submissions - don't reset
        setSelectedLocations([]);
        setGpsData(null);
        setGpsError(null);
        await loadInspections();
        await loadDueInspections();
        if (result && result.inspection_id) {
          navigate('/validate');
        }
      } else if (response.status === 504 || response.status === 502) {
        // Backend may have completed despite the gateway timeout. Pull latest record and show it here.
        try {
          const items = await api.loadInspections();
          if (items) {
            const startedAt = requestStartedAtMs;
            const candidates = items
              .filter(i => i && i.created_at)
              .map(i => ({ item: i, ts: Date.parse(i.created_at || 0) || 0 }))
              .filter(x => x.ts >= startedAt)
              .sort((a, b) => b.ts - a.ts);
            if (candidates.length > 0) {
              const latest = candidates[0].item;
              setInspectionResult({
                success: true,
                inspection_id: latest.id,
                analysis: latest.gemini_response || latest.analysis || {}
              });
              setSelectedImage(null);
              setLocation('');
              setNotes('');
              await loadInspections();
              await loadDueInspections();
              return; // Do not show the timeout alert since we recovered the result
            }
          }
        } catch (_) {
          // fall through to soft alert
        }
        await loadInspections();
        await loadDueInspections();
        showToast('⏳ Analysis took longer than expected. Please check the Validate or Data tabs for results.', 'info', 8000);
      } else {
        let message = 'Analysis failed';
        try {
          const error = await response.json();
          message = error.detail || message;
        } catch (_) {
          // Response was likely HTML (nginx error page). Keep default message.
        }
        showToast(`❌ ${message}`, 'error');
      }
    } catch (error) {
      console.error('Analysis failed:', error);
      
      // Save to offline queue if network request failed
      const offlineEmail = (submittedOnBehalfOf && typeof submittedOnBehalfOf === 'string' && submittedOnBehalfOf.includes('@'))
        ? submittedOnBehalfOf.trim().toLowerCase()
        : undefined;
      const submissionData = {
        image_base64: selectedImage.split(',')[1],
        location: location,
        notes: notes,
        submitted_by: submittedBy.trim() || 'Anonymous',
        submitted_by_email: offlineEmail,
        gps_data: gpsData,
        mode: 'technician',
        timestamp: new Date().toISOString(),
        source: 'technician_mode'
      };
      
      console.log('💾 Saving technician submission to offline queue due to network error...');
      saveToOfflineQueue(submissionData);
      showToast('📱 No internet connection. Submission saved offline and will be uploaded when connection is restored.', 'info', 6000);
      
      // Clear the form even though it's offline
      setSelectedImage(null);
      setLocation('');
      setNotes('');
      // Keep submittedBy for future submissions - don't reset
      setSelectedLocations([]);
      setGpsData(null);
      setGpsError(null);
    } finally {
      setIsAnalyzing(false);
      setShowProcessingModal(false);
      setAnalyzeProgress(100);
      setTimeout(() => setAnalyzeProgress(0), 1000);
    }
  };



  const handleLogout = () => {
    localStorage.removeItem('session_token');
    setUser(null);
    navigate('/');
  };

  // Progress animation while analyzing
  useEffect(() => {
    if (!showProcessingModal) return;
    let pct = 0;
    const timer = setInterval(() => {
      pct = Math.min(95, pct + Math.max(1, Math.floor(Math.random() * 6)));
      setAnalyzeProgress(pct);
    }, 500);
    return () => clearInterval(timer);
  }, [showProcessingModal]);

  // Fetch nearest affiliate when GPS exists
  useEffect(() => {
    const fetchNearest = async () => {
      if (!gpsData || !gpsData.latitude || !gpsData.longitude) return;
      try {
        setLoadingNearest(true);
        const data = await api.fetchNearestAffiliate(gpsData.latitude, gpsData.longitude, 1);
        setNearestOffice((data.offices && data.offices[0]) || null);
      } catch (_) {
        // ignore
      } finally {
        setLoadingNearest(false);
      }
    };
    fetchNearest();
  }, [gpsData, backendUrl]);

  if (loading) {
    return (
      <div className="min-h-screen relative bg-[var(--pbfs-navy)] flex items-center justify-center">
        <div className="flex flex-col items-center">
          <img 
            src="/images/PBFS-white.png" 
            alt="Pye-Barker Fire Safety" 
            className="w-48 h-auto mb-6 animate-pulse"
          />
          <div className="w-64 h-2 bg-[var(--pbfs-gray)] rounded-full overflow-hidden">
            <div className="h-full bg-[var(--pbfs-green)] animate-pulse" style={{ width: '70%' }}></div>
          </div>
          <div className="text-white text-lg mt-4">Loading TagQuest...</div>
        </div>
      </div>
    );
  }

  // SECURITY: App loads for all users - onboarding handles email collection
  // Protected features (admin, leaderboard) require MSAL authentication
  // Regular users authenticate via simple email entry during onboarding
  // The onboarding modal (in ModalManager) will prompt for email if needed

  return (
    <div 
      className="min-h-screen relative bg-[var(--pbfs-navy)]"
    >
      
      {/* Update Available Banner */}
      {showUpdateNotification && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 flex items-center justify-between shadow-lg">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">🔄</span>
            <div>
              <div className="font-bold">New Version Available!</div>
              <div className="text-sm text-white/90">Tap to reload and get the latest features</div>
            </div>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="bg-white text-blue-600 font-bold py-2 px-6 rounded-lg hover:bg-blue-50 transition-all transform hover:scale-105"
          >
            Reload Now
          </button>
        </div>
      )}
      {/* Header - Show on all routes */}
      <Header
        offlineQueue={offlineQueue}
        isProcessingOffline={isProcessingOffline}
        processOfflineQueue={processOfflineQueue}
        isFireExtinguisherCameraOpen={isFireExtinguisherCameraOpen}
        setIsFireExtinguisherCameraOpen={setIsFireExtinguisherCameraOpen}
      />

      {/* Main Content */}
      {/* 🔧 FIX: ONE Routes block (like November) - conditional blocks break routing! */}
      <main 
        className="relative z-10 container mx-auto px-4 py-6 min-w-[375px]" 
        style={{
          paddingTop: 'max(90px, calc(80px + env(safe-area-inset-top, 0px)))',
          paddingBottom: 'max(100px, calc(90px + env(safe-area-inset-bottom, 0px)))'
        }}
      >
        <Routes>
          {/* Admin Panel Login */}
          <Route path="/admin" element={<AdminPanel backendUrl={backendUrl} />} />
          
          {/* Protected Admin Routes */}
          <Route path="/admin/dashboard" element={
            <ProtectedRoute>
              <AdminDashboard backendUrl={backendUrl} />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/users" element={
            <ProtectedRoute>
              <UserManagement 
                backendUrl={backendUrl}
                onViewUserSubmissions={(userName, userEmail = null) => {
                  setSelectedUserForSubmissions(userName);
                  setSelectedUserEmailForSubmissions(userEmail);
                  setShowUserSubmissionsModal(true);
                }}
              />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/approvals" element={
            <ProtectedRoute>
              <ApprovalWorkflow backendUrl={backendUrl} />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/emails" element={
            <ProtectedRoute>
              <EmailDigestManagement backendUrl={backendUrl} />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/inbox" element={
            <ProtectedRoute>
              <GmailStyleInbox />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/regional-officers" element={
            <ProtectedRoute>
              <RegionalOfficersManagement backendUrl={backendUrl} />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/archive" element={
            <ProtectedRoute>
              <ArchiveView backendUrl={backendUrl} />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/trash" element={
            <ProtectedRoute>
              <TrashView backendUrl={backendUrl} />
            </ProtectedRoute>
          } />
          
          
          <Route path="/admin/analytics" element={
            <ProtectedRoute>
              <AnalyticsReports 
                backendUrl={backendUrl}
                onViewUserSubmissions={(userName) => {
                  setSelectedUserForSubmissions(userName);
                  setShowUserSubmissionsModal(true);
                }}
              />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/conversions" element={
            <ProtectedRoute>
              <ConversionMetrics />
            </ProtectedRoute>
          } />

          <Route path="/admin/campaigns" element={
            <ProtectedRoute>
              <CampaignManagement />
            </ProtectedRoute>
          } />

          {/* Default route - Dashboard */}
          <Route path="/" element={
            <DashboardPage
              showOnboarding={showOnboarding}
              isFireExtinguisherCameraOpen={isFireExtinguisherCameraOpen}
              setIsFireExtinguisherCameraOpen={setIsFireExtinguisherCameraOpen}
              isQuickShotMode={isQuickShotMode}
              showPyeGuide={showPyeGuide}
              setShowPyeGuide={setShowPyeGuide}
              showAdditionalNotes={showAdditionalNotes}
              setShowAdditionalNotes={setShowAdditionalNotes}
              isCustomBusinessEntry={isCustomBusinessEntry}
              setIsCustomBusinessEntry={setIsCustomBusinessEntry}
              showSuggestions={showSuggestions}
              setShowSuggestions={setShowSuggestions}
              locationSelectedFromPopup={locationSelectedFromPopup}
              isSubmitting={isSubmitting}
              inspectionResult={inspectionResult}
              isAnalyzing={isAnalyzing}
              canvasRef={canvasRef}
              fileInputRef={fileInputRef}
              mainPageFileInputRef={mainPageFileInputRef}
              isTestingCamera={isTestingCamera}
              setIsTestingCamera={setIsTestingCamera}
            />
          } />

          {/* Leaderboard page - Protected: requires PBFS tenant authentication */}
          <Route path="/leaderboard" element={
            <ProtectedUserRoute>
              <LeaderboardPage 
              onBack={() => {
                // Reset to fresh state
                setShowSuccessPopup(false);
                setSuccessPopupData(null);
                setSelectedImage(null);
                setLocation('');
                setNotes('');
                setBusinessName('');
                setQuickShotOtherNotes('');
                setMainPageAttachments([]);
                setInspectionResult(null);
                setCapturedImages([]);
                
                // Navigate back to dashboard
                navigate('/');
                
                // Immediately open camera for next scan
                setIsFireExtinguisherCameraOpen(true);
                
                // Auto-capture GPS
                if (!gpsData && !isCapturingGPS) {
                  captureGPS();
                }
              }} 
              onViewUserSubmissions={(userName) => {
                setSelectedUserForSubmissions(userName);
                setShowUserSubmissionsModal(true);
              }}
              backendUrl={backendUrl}
              leaderboardData={getPointsLeaderboard()}
              inspections={inspections}
              getSubmittedByName={getSubmittedByName}
              currentUser={currentUser}
              currentUserEmail={submittedOnBehalfOf}
              />
            </ProtectedUserRoute>
          } />

          {/* Direct deep link to open a submission - Protected: requires PBFS tenant authentication */}
          <Route path="/submission/:inspectionId" element={
            <ProtectedUserRoute>
              <DirectSubmissionPage
                backendUrl={backendUrl}
                currentUser={currentUser}
              />
            </ProtectedUserRoute>
          } />

          {/* Sales Lead Action Page - Magic link from email (no auth required) */}
          <Route path="/sales/lead/:token" element={<SalesLeadActionPage />} />
        </Routes>

        {/* Camera View - Show when camera is open */}
        {isFireExtinguisherCameraOpen && !useAutoCapture && !showOnboarding && routerLocation.pathname === '/' && (
          <FireExtinguisherCamera
            key={isFireExtinguisherCameraOpen ? 'camera-active' : 'camera-inactive'}
            currentUser={currentUser}
            userPoints={getUserTotalPoints()}
            userRank={getUserRank()}
            soundEnabled={soundEnabled}
            onToggleSound={toggleSound}
            onNavigateLeaderboard={() => {
              setIsFireExtinguisherCameraOpen(false);
              navigate('/leaderboard');
            }}
            onExit={() => setIsFireExtinguisherCameraOpen(false)}
            onAdminAccess={() => {
              setIsFireExtinguisherCameraOpen(false);
              navigate('/validate');
            }}
            onCapture={async (fullResData, thumbnailData) => {
              console.log('📷 Photo captured - Dual resolution: Full (1200×2000 PORTRAIT) + Thumbnail (300×500)');
              
              // 🔄 BUGFIX: Force reset state at start of each capture to ensure workflow restarts properly
              console.log('🔄 Resetting submission state for new capture...');
              setCapturedImages([]);
              setSelectedImage(null);
              setLocation('');
              setBusinessName('');
              setSelectedLocations([]);
              setNearestOffice(null);
              setLoadingNearest(false);
              setBusinessSuggestions([]);
              setIsLoadingSuggestions(false);
              setShowSuggestions(false);
              setShowLocationPopup(false);
              setShowConfirmationPopup(false);
              setConfirmationData(null);
              
              // Small delay to ensure state clears
              await new Promise(resolve => setTimeout(resolve, 50));
              
              // 🎬 START REAL PROGRESS TRACKING
              setShowRenderProgress(true);
              setRenderStep('capture');
              
              // 🔥 DISPATCH EVENT: Photo captured (for leaderboard real-time updates)
              window.dispatchEvent(new CustomEvent('photo-captured', {
                detail: { image: fullResData, timestamp: new Date().toISOString() }
              }));
              
              // NOW process the new capture - use full-res for display and AI processing
              setSelectedImage(fullResData);
              const newImage = {
                id: Date.now(),
                data: fullResData,        // Full-res for AI analysis
                thumbnailData: thumbnailData, // Thumbnail for lists/previews
                timestamp: new Date().toISOString(),
                size: fullResData.length,
                type: 'primary'
              };
              setCapturedImages([newImage]);
              
              // Close camera
              setIsFireExtinguisherCameraOpen(false);
              
              // Step 2: GPS
              setRenderStep('gps');
              const coords = await captureGPS();
              
              // Step 3: Fetch businesses
              setRenderStep('businesses');
              if (coords) {
                await fetchBusinessSuggestions(coords.latitude, coords.longitude, showToast);
                await new Promise(resolve => setTimeout(resolve, 100));
              } else {
                console.log('⚠️ GPS unavailable - user can enter location manually');
              }
              
              // Step 4: Render
              setRenderStep('render');
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // Hide progress, show modal
              setShowRenderProgress(false);
              setShowLocationPopup(true);
              console.log('✅ Location popup opened');
            }}
          />
        )}
      </main>

      {/* ModalManager - All modals centralized */}
      <ModalManager
        currentRoute={routerLocation.pathname}
        showOnboarding={showOnboarding}
        completeOnboarding={completeOnboarding}
        setQuickShotOtherNotes={setQuickShotOtherNotes}
        backendUrl={backendUrl}
        attributionMode={attributionMode}
        setAttributionMode={setAttributionMode}
        submittedBy={submittedBy}
        setSubmittedBy={setSubmittedBy}
        navigate={navigate}
        consecutiveScans={consecutiveScans}
        setConsecutiveScans={setConsecutiveScans}
        showGamification={showGamification}
        setIsFireExtinguisherCameraOpen={setIsFireExtinguisherCameraOpen}
        useAutoCapture={useAutoCapture}
        isFireExtinguisherCameraOpen={isFireExtinguisherCameraOpen}
        soundDebug={soundDebug}
        showRenderProgress={showRenderProgress}
        setShowRenderProgress={setShowRenderProgress}
        renderStep={renderStep}
        setRenderStep={setRenderStep}
        showLocationPopup={showLocationPopup}
        setShowLocationPopup={setShowLocationPopup}
        isCustomBusinessEntry={isCustomBusinessEntry}
        setIsCustomBusinessEntry={setIsCustomBusinessEntry}
        showSuggestions={showSuggestions}
        setShowSuggestions={setShowSuggestions}
        showConfirmationPopup={showConfirmationPopup}
        setShowConfirmationPopup={setShowConfirmationPopup}
        confirmationData={confirmationData}
        setConfirmationData={setConfirmationData}
        showSuccessPopup={showSuccessPopup}
        setShowSuccessPopup={setShowSuccessPopup}
        successPopupData={successPopupData}
        setSuccessPopupData={setSuccessPopupData}
        selectedImage={selectedImage}
        setSelectedImage={setSelectedImage}
        businessSuggestions={businessSuggestions}
        isLoadingSuggestions={isLoadingSuggestions}
        showConfirmationNotesPopup={showConfirmationNotesPopup}
        setShowConfirmationNotesPopup={setShowConfirmationNotesPopup}
      />

      {/* Preloaded Audio Elements (Hidden) */}
      <audio ref={(el) => audioRefs.current['camera-shutter'] = el} src="/sounds/camera-shutter.mp3" preload="auto" />
      <audio ref={(el) => audioRefs.current['level-up'] = el} src="/sounds/level-up.mp3" preload="auto" />
      <audio ref={(el) => audioRefs.current['scan'] = el} src="/sounds/scan.mp3" preload="auto" />
      <audio ref={(el) => audioRefs.current['badge'] = el} src="/sounds/badge.mp3" preload="auto" />

      {/* 🔔 Bottom Navigation (User-facing pages only) */}
      <BottomNav
        unreadInboxCount={userInboxUnreadCount}
        onInboxClick={() => setShowUserInboxModal(true)}
        onMapClick={() => setShowMapModal(true)}
      />
      
      {/* 🔔 User Inbox Modal */}
      <UserInbox
        isOpen={showUserInboxModal}
        onClose={() => {
          setShowUserInboxModal(false);
          // Refresh count after closing
          const userEmail = getNotificationUserEmail();
          if (userEmail) {
            api.fetchUserNotificationsCount(userEmail)
              .then(d => setUserInboxUnreadCount(d.unread_count || 0))
              .catch(() => {});
          }
        }}
        onReadUpdate={() => {
          // 🔔 Refresh badge count immediately when a notification is marked as read
          const userEmail = getNotificationUserEmail();
          if (userEmail) {
            api.fetchUserNotificationsCount(userEmail)
              .then(d => setUserInboxUnreadCount(d.unread_count || 0))
              .catch(() => {});
          }
        }}
        userEmail={getNotificationUserEmail()}
      />
      
      {/* 🗺️ User Map Modal */}
      <UserMapModal
        isOpen={showMapModal}
        onClose={() => setShowMapModal(false)}
      />

      {/* 📋 User Submissions Modal - Leaderboard name click */}
      <UserSubmissionsModal
        isVisible={showUserSubmissionsModal}
        userName={selectedUserForSubmissions}
        userEmail={selectedUserEmailForSubmissions}
        adminMode={Boolean(selectedUserEmailForSubmissions)}
        onClose={() => {
          setShowUserSubmissionsModal(false);
          setSelectedUserForSubmissions(null);
          setSelectedUserEmailForSubmissions(null);
        }}
        getSubmittedByName={getSubmittedByName}
      />
      
      {/* 📝 Info Request Modal - "Need More Information" */}
      <InfoRequestModal
        isVisible={showInfoRequestModal}
        aiData={infoRequestData}
        imageData={infoRequestData?.image}
        onCancel={() => {
          setShowInfoRequestModal(false);
          setInfoRequestData(null);
          // Show success anyway (user can skip correction)
          setShowSuccessPopup(true);
        }}
        onResubmit={(correctedData) => {
          console.log('📝 User submitted corrected data');
          setShowInfoRequestModal(false);
          
          // Resubmit with user corrections
          handleQuickShotSubmit(
            playSound,
            showToast,
            setSubmissionBanner,
            submittedOnBehalfOf || currentUser,
            infoRequestData?.image,
            {
              setSuccessPopupData,
              setShowSuccessPopup
            },
            correctedData.businessName,  // businessNameOverride
            correctedData.notes,  // notesOverride
            correctedData.address,  // addressOverride
            correctedData.contactPhone,  // phoneOverride
            'manual_entry',  // locationSource
            gpsData,  // gpsData
            correctedData.serialNumber  // serialNumberOverride
          );
        }}
      />

    </div>
  );
};

export default App;
